import React, { useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from './AlertDialog';

interface ConfirmDeleteDialogProps {
  onConfirm?: () => void;
  /** Opt-in async confirmation; the caller reports mutation errors. */
  onConfirmAsync?: () => Promise<unknown>;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  disabled?: boolean;
  children: React.ReactNode;
}

const ConfirmDeleteDialog = ({
  onConfirm,
  onConfirmAsync,
  title = 'Opravdu chcete položku odstranit?',
  description = 'Tato akce je nevratná.',
  confirmText = 'Ano',
  cancelText = 'Ne',
  disabled = false,
  children,
}: ConfirmDeleteDialogProps) => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const confirming = useRef(false);

  const confirmAsync = async () => {
    confirming.current = true;
    setPending(true);
    try {
      await onConfirmAsync!();
      setOpen(false);
    } catch {
      // The mutation reports the error; keep the dialog open for retry.
    } finally {
      confirming.current = false;
      setPending(false);
    }
  };

  const handleConfirm = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!onConfirmAsync) {
      onConfirm?.();
      return;
    }
    event.preventDefault();
    if (!confirming.current) void confirmAsync();
  };

  return (
    <AlertDialog open={open} onOpenChange={(value) => !confirming.current && setOpen(value)}>
      <AlertDialogTrigger asChild disabled={disabled}>
        {children}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-delete-cancel-btn" disabled={pending}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-delete-confirm-btn"
            onClick={handleConfirm}
            disabled={pending}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDeleteDialog;
